import { h } from "preact";
import { useEffect, useState } from "preact/hooks";
import { useSelector } from "react-redux";

import questionnaire from "../api/questionnaire";
import { sendError } from "../errors";

import Alert from "./Alert.jsx";
import Question from "./Question.jsx";
import LinearLoadingIndicator from "./LinearLoadingIndicator.jsx";
import i18n from "../i18n";
import { setLoading } from "../store";

export default function Questionaire(props) {
  const [alternative, setAlternative] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [question, setQuestion] = useState(null);
  const [isFinished, setIsFinished] = useState(false);
  const [isFirstQuestion, setIsFirstQuestion] = useState(false);
  const [error, setError] = useState(null);
  const [currentQuestionNumber, setCurrentQuestionNumber] = useState(null);
  const [maxQuestionCount, setMaxQuestionCount] = useState(null);
  const loading = useSelector((state) => state.ui.loading);

  function handleSelectionChange(value, useAlternative = false) {
    setAnswer(value);
    setAlternative(useAlternative);
  }

  function parseFetchResult(data) {
    setQuestion(data.question);
    setIsFirstQuestion(data.firstQuestion);
    setIsFinished(data.isFinished);
    setCurrentQuestionNumber(data.currentQuestionNumber);
    setMaxQuestionCount(data.maxQuestionCount);

    setAlternative(!!data.answer?.alternative);
    setAnswer(
      data.answer?.alternative
        ? ""
        : data.answer?.value ||
            data.question.template.options.initialValue ||
            ""
    );
  }

  async function loadData() {
    setLoading(true);
    try {
      setError(null);
      parseFetchResult(await questionnaire.fetch(props.qnaireId));
    } catch (err) {
      sendError(err);
      setError("Could not load data");
    }
    setLoading(false);
  }

  async function handleBackClick(event) {
    event.preventDefault();

    // This only works if there are no hidden questions in a quiz
    if (isFirstQuestion) {
      if (props.onBack) {
        props.onBack(event);
      }
      return;
    }

    setLoading(true);
    try {
      setError(null);
      const data = await questionnaire.back(props.qnaireId);
      // If backend tells us, it was the first question already, we go to previous module
      if (data.previousOnFirstQuestion) {
        return props.onBack(event);
      }
      parseFetchResult(data);
    } catch (err) {
      sendError(err);
      setError("Could not go back");
    }
    setLoading(false);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setLoading(true);
    try {
      setError(null);
      const data = await questionnaire.answer(
        props.qnaireId,
        answer,
        alternative
      );
      parseFetchResult(data);
      if (data.isFinished) {
        props.onNext();
      }
    } catch (err) {
      sendError(err);
      setError(
        (err.response && err.response.error) || "Could not submit answer"
      );
    }
    setLoading(false);
  }

  async function handleSkip(event) {
    event.preventDefault();
    setLoading(true);
    try {
      setError(null);
      const data = await questionnaire.skipQuestion(props.qnaireId);
      parseFetchResult(data);
      if (data.isFinished) {
        props.onNext();
      }
    } catch (err) {
      sendError(err);
      setError(
        (err.response && err.response.error) || "Could not skip question"
      );
    }
    setLoading(false);
  }

  useEffect(() => {
    setAnswer(props.answer);
    loadData(props.qnaireId);
  }, [props.qnaireId]);

  if (loading || isFinished) {
    return null;
  }
  return (
    <div>
      {error && (
        <Alert class="mb-16" error>
          {error}
        </Alert>
      )}
      <div class="t-body3 mb-16">{i18n.t(`tasks.${props.taskId}.tagline`)}</div>
      <LinearLoadingIndicator
        progress={
          (maxQuestionCount &&
            currentQuestionNumber &&
            (currentQuestionNumber / maxQuestionCount) * 100) ||
          0
        }
        class="questionnaire__progress-indicator mb-32"
        jadeGrey
      />
      {question && (
        <Question
          answer={answer}
          optional={question.optional}
          question={question}
          alternative={alternative}
          onSelectionChange={handleSelectionChange}
          onSubmit={handleSubmit}
          onBack={handleBackClick}
          skippable={question.skippable}
          onSkip={handleSkip}
          currentQuestionNumber={currentQuestionNumber}
        />
      )}
    </div>
  );
}
